<template>
  <!-- Desktop 3-col grid -->
  <div class="hidden md:grid md:grid-cols-3 md:gap-5">
    <a
      v-for="(item, index) in items"
      :key="index"
      :class="getCardClasses(item)"
      :href="item.link?.url"
      :target="item.link?.openInNewTab ? '_blank' : '_self'"
      :rel="item.link?.openInNewTab ? 'noopener noreferrer' : undefined"
    >
      <div class="flex flex-col gap-y-4">
        <div v-if="item.faSymbol?.length">
          <i :class="getIconClasses(item)" aria-hidden="true"></i>
        </div>

        <h3
          class="hyphens-auto text-f3 leading-tight xs:break-words"
          v-html="item.heading"
        ></h3>

        <template v-if="item.richText">
          <div
            class="ckeditor-content default-color"
            v-html="item.richText"
          ></div>
        </template>
      </div>

      <div v-if="item.link" class="mt-8">
        <div
          :class="item.cta ? 'inverted-link' : 'link'"
          class="overpass-medium"
        >
          <span class="mr-1"> > </span>
          <span class="link-text">{{ item.link.text }}</span>
        </div>
      </div>
    </a>
  </div>

  <!-- Mobile swipeable cards -->
  <div class="md:hidden">
    <div class="relative overflow-hidden">
      <div
        class="flex transition-transform duration-300 ease-out"
        :style="{ transform: `translateX(-${activeIndex * 100}%)` }"
        @touchstart.passive="onTouchStart"
        @touchend.passive="onTouchEnd"
      >
        <a
          v-for="(item, index) in items"
          :key="index"
          :class="getCardClasses(item, true)"
          :href="item.link?.url"
          :target="item.link?.openInNewTab ? '_blank' : '_self'"
          :rel="item.link?.openInNewTab ? 'noopener noreferrer' : undefined"
        >
          <div class="flex flex-col gap-y-4">
            <div v-if="item.faSymbol?.length">
              <i :class="getIconClasses(item)" aria-hidden="true"></i>
            </div>

            <h3
              class="hyphens-auto text-f3 font-bold leading-tight xs:break-words"
              v-html="item.heading"
            ></h3>

            <template v-if="item.richText">
              <div
                class="ckeditor-content default-color"
                v-html="item.richText"
              ></div>
            </template>
          </div>

          <div v-if="item.link" class="mt-8">
            <div
              class="overpass-medium"
              :class="item.cta ? 'inverted-link' : 'link'"
            >
              <span class="mr-1">&gt; </span>{{ item.link.text }}
            </div>
          </div>
        </a>
      </div>
    </div>

    <!-- indicators -->
    <div v-if="items.length > 1" class="ml-2 mt-4 flex justify-start gap-2">
      <button
        v-for="(item, index) in items"
        :key="'dot-' + index"
        type="button"
        class="transition-colors"
        :class="getIndicatorClasses(index)"
        @click="goTo(index)"
        :aria-label="'Element ' + (index + 1)"
      ></button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';

interface CardLink {
  url: string;
  text: string;
  openInNewTab: boolean;
}

interface CardItem {
  faSymbol?: string;
  heading: string;
  lead: string;
  richText?: string;
  cta: boolean;
  link?: CardLink;
}

const props = defineProps<{
  items: CardItem[];
  regularBg: string;
  highlightBg: string;
}>();

const activeIndex = ref(0);
let touchStartX = 0;

const goTo = (index: number) => {
  if (index < 0 || index >= props.items.length) return;
  activeIndex.value = index;
};

const onTouchStart = (e: TouchEvent) => {
  touchStartX = e.touches[0].clientX;
};

const onTouchEnd = (e: TouchEvent) => {
  const dx = e.changedTouches[0].clientX - touchStartX;
  const threshold = 50; // px

  if (dx > threshold && activeIndex.value > 0) {
    goTo(activeIndex.value - 1);
  } else if (dx < -threshold && activeIndex.value < props.items.length - 1) {
    goTo(activeIndex.value + 1);
  }
};

const getCardClasses = (item: CardItem, isMobile: boolean = false) => {
  const base =
    'flex flex-col justify-between p-8 text-black' +
    (isMobile ? ' shrink-0 w-full' : '') +
    (item.link ? ' link-card' : '');

  if (item.cta) {
    return [base, props.highlightBg, 'text-white'];
  }

  const bg = props.regularBg;
  return [base, bg];
};

const getIconClasses = (item: CardItem) => {
  const base = 'fa-light mb-2 text-f3';
  if (item.cta) {
    return [base, 'text-white', item.faSymbol || ''];
  }
  return [base, 'text-primary', item.faSymbol || ''];
};

const getIndicatorClasses = (index: number) => {
  const base = 'h-2 w-2 rounded-full';

  if (activeIndex.value !== index && props.regularBg === 'bg-lightgray') {
    return [base, 'bg-lightgray'];
  } else if (
    activeIndex.value !== index &&
    props.regularBg !== 'bg-lightgray'
  ) {
    return [base, 'bg-white'];
  } else {
    return [base, 'bg-black'];
  }
};
</script>

<style scoped></style>
